import { createAsyncThunk } from '@reduxjs/toolkit';
import i18n from 'i18next';
import { RootState } from '../store';
import { notify } from '../app/appSlice';
import apiVRR from '../../libs/axios/apis';
import endPoints from '../../services/endpoints';
import {
  ISaveUserProfile,
  ISaveUserProfileNewPassword,
  TBrandLabel
} from './types';

export const fetchUserProfile = createAsyncThunk(
  'user/fetchProfile',
  async (_, { getState }) => {
    const userId = (getState() as any).app.vrr.id;
    const response = await apiVRR().get(endPoints.USER.PROFILE(userId));
    return response.data.data;
  }
);

export const updateUserProfile = createAsyncThunk(
  'user/updateProfile',
  async ({ email, username }: ISaveUserProfile, { getState }) => {
    const userProfileStates = {
      email,
      username
    };
    const userId = (getState() as any).app.vrr.id;
    await apiVRR().put(endPoints.USER.PROFILE(userId), userProfileStates);
    return userProfileStates;
  }
);

export const updateUserProfilePassword = createAsyncThunk(
  'user/updatePassword',
  async (
    { oldPassword, newPassword }: ISaveUserProfileNewPassword,
    { dispatch, getState }
  ) => {
    const userId = (getState() as any).app.vrr.id;
    const response = await apiVRR().put(endPoints.USER.PROFILE(userId), {
      oldPassword,
      newPassword
    });
    if (response.status >= 200 && response.status < 400) {
      dispatch(notify({ type: 'success' }));
    }
    return response;
  }
);

export const fetchConnectedAccountURL = createAsyncThunk(
  'user/fetchConnectedAccountURL',
  async (_, { getState }) => {
    const userId = (getState() as any).app.vrr.id;
    return apiVRR().get(
      endPoints.ACCOUNTS_ENDPOINTS.CONNECTED_ACCOUNT_PROMPT(userId)
    );
  }
);

export const fetchBrandLabels = createAsyncThunk(
  'brand/fetchAccountLabels',
  async (brandId: string) => {
    const response = await apiVRR('v2').get(
      endPoints.ACCOUNTS_ENDPOINTS.LABELS(brandId)
    );

    return response.data;
  }
);

export const refreshAccountLabels = createAsyncThunk(
  'user/refreshAccountLabels',
  async (brandId: string, { dispatch }) => {
    const response = await apiVRR('v2').post(
      endPoints.ACCOUNTS_ENDPOINTS.REFRESH_LABELS(brandId)
    );
    if (response.status >= 200 && response.status < 400) {
      dispatch(
        notify({
          type: 'success',
          options: {
            message: i18n.t(
              `components.connected_account.modal.notifications.refresh_success`
            )
          }
        })
      );
    }

    return response.data;
  }
);

export const fetchConnectedAccountCallback = createAsyncThunk(
  'user/fetchConnectedAccountCallback',
  async ({ code }: any, { getState }) => {
    const userId = (getState() as any).app.vrr.id;
    return apiVRR().get(
      endPoints.ACCOUNTS_ENDPOINTS.CONNECTED_ACCOUNT_CALLBACK(userId),
      {
        params: code
      }
    );
  }
);

export const displayBrands = createAsyncThunk(
  'connectedBrands/displayBrands',
  async (brand_ids: string[]) => {
    return apiVRR('v2').patch(
      endPoints.CONNECTED_BRANDS.CONNECTED_BRANDS_DISPLAY,
      {
        brand_ids
      }
    );
  }
);

export const manageBrands = createAsyncThunk(
  'connectedBrands/hideBrands',
  async (
    {
      toHideBrands,
      toDisplayBrands
    }: { toHideBrands: string[]; toDisplayBrands: string[] },
    { dispatch }
  ) => {
    const response = await apiVRR('v2').patch(
      endPoints.CONNECTED_BRANDS.CONNECTED_BRANDS_HIDE,
      {
        brand_ids: toHideBrands
      }
    );

    dispatch(displayBrands(toDisplayBrands));

    return response;
  }
);

export const displayAccounts = createAsyncThunk(
  'connectedAccounts/displayAccounts',
  async (account_ids: string[], { getState }) => {
    const brand_id = (getState() as any).setting.vrr_preferences
      .current_brand_id;
    return apiVRR('v2').patch(
      endPoints.CONNECTED_ACCOUNTS.CONNECTED_ACCOUNTS_DISPLAY(brand_id),
      {
        account_ids
      }
    );
  }
);

export const manageAccounts = createAsyncThunk(
  'connectedAccounts/hideAccounts',
  async (
    {
      toHideAccounts,
      toDisplayAccounts
    }: { toHideAccounts: string[]; toDisplayAccounts: string[] },
    { dispatch, getState }
  ) => {
    const brand_id = (getState() as any).setting.vrr_preferences
      .current_brand_id;
    const response = await apiVRR('v2').patch(
      endPoints.CONNECTED_ACCOUNTS.CONNECTED_ACCOUNTS_HIDE(brand_id),
      {
        account_ids: toHideAccounts
      }
    );

    dispatch(displayAccounts(toDisplayAccounts));

    return response;
  }
);

export const getUserProfile = (state: RootState): any => {
  return state.userProfile;
};

export const getBrandLabels = (state: RootState): TBrandLabel[] | undefined => {
  return state.userProfile.connectedAccount.brandLabels;
};

export const getConnectedAccountURL = (state: RootState): string | string[] => {
  return state.userProfile.connectedAccount.url;
};

export const isConnectedToAccount = (state: RootState): boolean => {
  return state.userProfile.connectedAccount.isConnected;
};
